/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable array-callback-return */
import { canUseDOM, chaseCodes, amexCodes } from '@marriott/mi-ui-library';
import { CardType, SELECTOR_LIST } from '../../constants/constants';
import {
  CardData,
  CardEntry,
  ComparisonObject,
} from '../../../molecules/CardComparisonAccordion/CardComparisonAccordion.types';
import { targetPayloadRequest, useSession } from '@marriott/mi-headless-utils';
import { RefObject, useRef } from 'react';
import { FeatureIcon, FeatureIconColor, iconColorMap } from './enums';
import { useEffect, useState } from 'react';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { isEmpty } from '@marriott/shared/mi-helper-utils';
import { useRouter } from 'next/router';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _satellite: Record<string, any>;
    adobeDataLayer: {
      [key: number]: {
        event: string;
        [key: string]: Record<string, unknown> | string | undefined | boolean;
      };
      version: number;
      push: (event: { event: string; [key: string]: Record<string, unknown> | string | undefined | boolean }) => void;
      getState: () => Record<string, unknown>;
      addEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
      removeEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
    };
  }
}

//Method to return column class helper for various components
export const getColumnClass = (columnLayout: string, tabType: string): string => {
  let customClass = '';
  if (columnLayout === '1-column') {
    customClass = 'col-12 ';
  } else if (columnLayout === '3-column') {
    if (tabType === 'personal') {
      customClass = 'col-6 col-md-4';
    } else {
      customClass = 'col-12 col-md-4';
    }
  } else {
    customClass = 'col-6';
  }
  return customClass;
};

export function getDataLayerScript(dataLayer: { data: unknown[]; mvpOffersData: string }): string {
  return `var dataLayer = ${dataLayer?.data ? JSON.stringify(dataLayer?.data[0]) : '{}'}; var mvpOffers = ${
    dataLayer?.mvpOffersData && dataLayer?.mvpOffersData !== 'null' ? dataLayer?.mvpOffersData : '{}'
  };`;
}

//Method to remove extra spacing in on resize
export const removeExtraHeights = (ref: RefObject<HTMLDivElement>): void => {
  if (canUseDOM) {
    SELECTOR_LIST?.forEach(({ selector, rowCount }) => {
      for (let row = 1; row <= rowCount; row++) {
        const requiredElem = ref?.current?.querySelectorAll(`.${selector}-${row}`);
        if (requiredElem && requiredElem.length) {
          requiredElem.forEach(node => {
            const element = node as HTMLElement;
            if (element?.style) {
              element.style.minHeight = '';
            }
          });
        }
      }
    });
  }
};

//Method to equalize height of points section elements
const equalizeCardHeight = (selector: string, rowCount: number, ref: RefObject<HTMLDivElement>): void => {
  if (canUseDOM) {
    for (let row = 1; row <= rowCount; row++) {
      const allNodes = ref?.current?.querySelectorAll(`.${selector}-${row}`);
      let maxHeight = 0;
      if (allNodes && allNodes.length) {
        allNodes.forEach(node => {
          const nodeHeight = node.clientHeight;
          if (nodeHeight > maxHeight) {
            maxHeight = nodeHeight;
          }
        });
        allNodes.forEach(node => {
          const element = node as HTMLElement;
          if (element?.style) {
            element.style.minHeight = `${maxHeight}px`;
          }
        });
      }
    }
  }
};

export const equalizeBankLandingCards = (ref: RefObject<HTMLDivElement>) => {
  SELECTOR_LIST?.forEach(currentElem => {
    const { selector, rowCount } = currentElem;
    equalizeCardHeight(selector, rowCount, ref);
  });
};

export const referAFriend = (dataLayer: Record<string, unknown>, ref: RefObject<HTMLDivElement>): void => {
  const isAuthenticated = dataLayer?.['mr_prof_authentication_state'] === 'authenticated';
  if (!isAuthenticated) return;

  const cardDetails = dataLayer['mr_prof_cc_all'];
  const detailsArray = cardDetails ? String(cardDetails).split(',') : [];
  const cardDetailsElements = ref?.current?.querySelectorAll('.refer-friend-link');

  if (!cardDetailsElements?.length) return;

  const ccFromDom: Array<string | undefined> = [];

  cardDetailsElements.forEach(ele => {
    const tempEle = ele as HTMLElement;
    ccFromDom.push(tempEle.dataset?.['creditcardcode']?.trim());
  });

  const matchingCodes = ccFromDom
    .filter((code): code is string => Boolean(code))
    .filter(code => detailsArray.includes(code));

  matchingCodes.forEach(code => {
    cardDetailsElements.forEach(ele => {
      const tempEle = ele as HTMLElement;
      const cardCodeForEle = tempEle.dataset?.['creditcardcode']?.trim();
      if (cardCodeForEle === code) {
        tempEle.classList.remove('d-none');
      }
    });
  });
};
// Returns user's country code from Adobe Data Layer (profile country for authenticated, Akamai country for guests)
export const getUserLocation = (adobeDataLayer?: unknown): string | undefined => {
  const dataLayer = (adobeDataLayer || global?.window?.dataLayer) as Record<string, unknown>;

  if (dataLayer?.['memState'] === 'authenticated') {
    return dataLayer?.['mr_prof_address_country'] as string | undefined;
  }

  return dataLayer?.['browser_akamai_loc_country'] as string | undefined;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setObserver = (componentId: any, ref: RefObject<HTMLDivElement>) => {
  const pageWcmMode = document.getElementsByTagName('html')[0];
  const updateStyleComponent = ref?.current?.querySelector(`#${componentId}__slides`);
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.attributeName === 'class') {
        const currWcmMode = pageWcmMode?.getAttribute('class');
        if (currWcmMode?.includes('Edit')) {
          updateStyleComponent?.classList.add('glide__slides_authoring');
        } else if (currWcmMode?.includes('Preview')) {
          updateStyleComponent?.classList.remove('glide__slides_authoring');
        }
      }
    });
  });
  observer.observe(pageWcmMode, { attributes: true });

  return () => {
    observer.disconnect();
  };
};

export const shouldCardDisplay = (cardType: string, activeTab: string): boolean => {
  let displayClass = true;
  // Early return for empty values
  if (!cardType || !activeTab) {
    return displayClass;
  }

  // Card type must match active tab, otherwise hide
  if ((cardType === 'business' || cardType === 'personal') && cardType !== activeTab) {
    displayClass = false;
  }

  return displayClass;
};

// helper function to get icon color based on status
export const getIconColor = (status: FeatureIcon): FeatureIconColor => {
  return iconColorMap[status];
};

/**
 * Function to determine if the user is on a mobile device
 */
export const isMobile = (): boolean => {
  // handle server-side rendering case
  if (typeof navigator === 'undefined') {
    return false;
  }
  const isTablet = !!(/Linux/i.test(navigator.userAgent) && navigator?.maxTouchPoints > 1);
  const isIpad = !!(
    /Macintosh/i.test(navigator.userAgent) &&
    navigator?.maxTouchPoints < 6 &&
    navigator?.maxTouchPoints > 0
  );
  return (
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || isTablet || isIpad
  );
};

/**
 * Custom hook to detect if the device is in landscape orientation.
 * Uses matchMedia API to listen for orientation changes.
 * Returns false during SSR.
 */
export const useLandscapeOrientation = (): boolean => {
  // Initialize with the correct value on client-side to avoid hydration mismatch flash
  const getInitialLandscape = () => {
    if (!canUseDOM) return false;
    return window.matchMedia('(orientation: landscape)').matches;
  };

  const [isLandscape, setIsLandscape] = useState(getInitialLandscape);

  useEffect(() => {
    if (!canUseDOM) return;

    const landscapeQuery = window.matchMedia('(orientation: landscape)');
    // Set initial value in case it wasn't set correctly during SSR
    setIsLandscape(landscapeQuery.matches);

    const handleOrientationChange = (event: MediaQueryListEvent) => {
      setIsLandscape(event.matches);
    };

    landscapeQuery.addEventListener('change', handleOrientationChange);
    return () => landscapeQuery.removeEventListener('change', handleOrientationChange);
  }, []);

  return isLandscape;
};

/**
 * Function extracts a paragraph that includes "Annual Fee" in <p></p> tag from the authored html code
 */
export const extractAnnualFee = (innerHtml: string): string | undefined => {
  if (!innerHtml) {
    return undefined;
  }

  const lower = innerHtml.toLowerCase();
  let searchFrom = 0;
  let pOpen = lower.indexOf('<p', searchFrom);

  // Loop while we can find more <p> tags
  while (pOpen !== -1) {
    const gt = lower.indexOf('>', pOpen + 2);
    if (gt === -1) return undefined;

    const pClose = lower.indexOf('</p>', gt + 1);
    if (pClose === -1) return undefined;

    const content = innerHtml.slice(gt + 1, pClose);
    // Case-insensitive includes without regex
    if (content.toLowerCase().includes('annual fee')) {
      return innerHtml.slice(pOpen, pClose + 4);
    }

    // Move to next paragraph
    searchFrom = pClose + 4;
    pOpen = lower.indexOf('<p', searchFrom);
  }

  // No paragraph with "annual fee" found
  return undefined;
};

/**
 * Type guard to verify if an entry is a valid CardData object
 * @param entry - Unknown value to check
 * @returns True if entry is a CardData object
 */
const isCardDataEntry = (entry: unknown): entry is CardData => {
  return typeof entry === 'object' && entry !== null && !Array.isArray(entry);
};

/**
 * Transforms a ComparisonObject into a flat array of CardData objects
 * @param cardArray - The comparison object containing card entries
 * @returns Flattened array of CardData with proper cardId and cardType
 */
export const setCardArray = (cardArray?: ComparisonObject): CardData[] => {
  if (!cardArray) {
    return [];
  }

  const dataArray: CardData[] = [];

  Object.entries(cardArray).forEach(([cardKey, cardValue]) => {
    // Skip categories and null/undefined values
    if (cardKey === 'categories' || !cardValue) {
      return;
    }

    // Extract card type from the card entry
    const entries = Object.values(cardValue).filter(isCardDataEntry);
    const defaultCardType: CardType = (cardValue as CardEntry).cardType ?? 'none';

    entries.forEach(data => {
      dataArray.push({
        ...data,
        cardId: data.cardId ?? cardKey,
        cardType: (data.cardType as CardType | undefined) ?? defaultCardType,
      });
    });
  });

  return dataArray;
};

export const replaceWhitespaceAndAddBtn = (componentId: string | undefined): string => {
  if (componentId === undefined) {
    return '';
  }
  return componentId.replace(/\s+/g, '-') + '-btn';
};

export const useAlloyTargetMethod = (targetMboxValue?: string) => {
  const router = useRouter();
  const queryParams = router.query;
  const DST_VALUES = [...amexCodes, ...chaseCodes];
  const isDSTvalue = DST_VALUES.some(key =>
    Object.keys(queryParams).some(param => param.toLowerCase() === key?.toLowerCase())
  );

  const [isTargetContentLoaded, setIsTargetContentLoaded] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [targetData, setTargetData] = useState<any>('');
  const { datalayer, mvpOffers, updateDatalayer } = useSession();
  let counter = 0;
  const hasCalledFetchTargetData = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isEmpty(datalayer) && !hasCalledFetchTargetData.current) {
      hasCalledFetchTargetData.current = true;

      if (targetMboxValue && !isDSTvalue) {
        launchLoadedAddEventListener();
      } else {
        setIsTargetContentLoaded(true);
      }
    }
  }, [datalayer]);

  if (!targetMboxValue) {
    return { isTargetContentLoaded: true, targetData: '' };
  }

  const launchLoadedAddEventListener = () => {
    const handleLaunchLoaded = () => {
      if (counter < 1) {
        fetchTargetData(targetMboxValue);
        counter = counter + 1;
      }
    };

    if (window !== undefined && window.adobeDataLayer) {
      window?.adobeDataLayer?.addEventListener('launchLoaded', handleLaunchLoaded);
    }

    const timeoutId = setTimeout(() => {
      if (window !== undefined) {
        window?.adobeDataLayer?.removeEventListener('launchLoaded', handleLaunchLoaded);
        setIsTargetContentLoaded(true);
      }
    }, 10000);

    return () => {
      clearTimeout(timeoutId);
      if (window !== undefined && window.adobeDataLayer) {
        window?.adobeDataLayer?.removeEventListener('launchLoaded', handleLaunchLoaded);
      }
    };
  };

  const fetchTargetData = async (targetMboxValue?: string) => {
    const targetMbox = targetMboxValue && [targetMboxValue];
    const payLoad = targetPayloadRequest(
      {
        isUxl: 'false',
        locale:
          window?.sessionData?.locale?.replace('-', '_') ||
          window?._satellite?.['getVar']('brwsrLang') ||
          datalayer?.['brwsrLang'] ||
          'en_US',
        mboxName: targetMbox,
        propertyToken: useClientEnvVarsStore.getState().envVarsObject['TARGET_PROPERTY_TOKEN'] as string,
      },
      datalayer,
      mvpOffers
    );
    try {
      const result = await window?.alloy<{ propositions?: unknown[] }>('sendEvent', {
        data: {
          __adobe: {
            target: payLoad?.execute?.mboxes?.[0]?.parameters,
          },
        },
        xdm: {},
        decisionScopes: targetMbox,
      });

      const propositions = result?.propositions;
      let item: Record<string, unknown> | undefined;
      let experience: string | undefined;
      let foundProposition = false;

      if (propositions && targetMboxValue) {
        for (const prop of propositions) {
          const proposition = prop as Record<string, unknown>;
          const propItems = proposition?.['items'] as unknown[];
          const firstItem = propItems?.[0] as Record<string, unknown>;
          const itemScope = proposition?.['scope'] as string;
          item = proposition;

          if (itemScope === targetMboxValue && firstItem) {
            const data = firstItem?.['data'] as Record<string, unknown>;
            const content = data?.['content'] as Record<string, unknown>;
            const meta = firstItem?.['meta'] as Record<string, unknown>;
            const items = content?.[':items'] as Record<string, unknown>;
            const root = items?.['root'] as Record<string, unknown>;
            const rootItems = root?.[':items'] as Record<string, unknown>;
            const responsivegrid = rootItems?.['responsivegrid'] as Record<string, unknown>;
            const responsivegridItems = responsivegrid?.[':items'];

            console.log('PROPOSITION SELECTED', responsivegridItems);
            experience = meta?.['experience.name'] as string;
            console.log('Experience name', experience);
            setTargetData(responsivegridItems as Record<string, unknown>);
            foundProposition = true;
          }
        }

        setIsTargetContentLoaded(true);

        if (foundProposition) {
          updateDatalayer({
            event: 'card-layout-updated',
            data: { item, experience },
            skipDatalayerUpdate: true,
          } as any); // eslint-disable-line @typescript-eslint/no-explicit-any
        }
      }

      if (item) {
        window?.alloy<unknown>('sendEvent', {
          xdm: {
            eventType: 'decisioning.propositionDisplay',
            _experience: {
              decisioning: {
                propositions: [
                  {
                    id: item?.['id'],
                    scope: item?.['scope'],
                    scopeDetails: item?.['scopeDetails'],
                  },
                ],
                propositionEventType: {
                  display: 1,
                },
              },
            },
          },
        });
      }
    } catch (error) {
      console.log('Error occurred in Target call', error);
      setIsTargetContentLoaded(true);
    }
  };

  return { isTargetContentLoaded, targetData: targetData };
};

/**
 * Normalize AEM boolean values which can come as boolean, string, or empty string
 *
 * @param value - The value from AEM (can be boolean, string 'true'/'false', or empty string)
 * @returns Normalized boolean value
 */
export const normalizeAEMBoolean = (value: boolean | string | undefined): boolean => {
  // Handle empty string (AEM's representation of false)
  if (value === '') return false;

  // Handle string representations
  if (value === 'true') return true;
  if (value === 'false') return false;

  // Handle actual booleans
  if (typeof value === 'boolean') return value;

  // Default to false for undefined/null
  return false;
};
